import React, { useEffect, useCallback, useRef } from "react";
import Chart from "../chart.js";
import Chart2 from "../chart2.js";
import SurveyIntro from "./surveyIntro.js";
import UserInfo from "./userInfo.js";
import html2canvas from "html2canvas";
import { getSurveyPdf } from "services/_survey.js";
import { errorToast } from "components/toast";
import { getSurveyQuestions } from "services/_survey";
import logo from "../../../assets/images/Logo5.svg";
import { svgToPngBase64 } from "./emailDialog.js";

export const PRIOR_DIAGNOSIS_ID = "prior_dx";
export const PRIOR_DIAGNOSIS_QUESTION =
  "Have you previously been diagnosed with axial spondyloarthritis (axSpA), ankylosing spondylitis (AS), or non-radiographic axial spondyloarthritis (nr-axSpA) by a healthcare provider?";

export const captureChartAsBase64 = async (chartRef, type) => {
  if (!chartRef?.current) return null;

  const cloned = chartRef.current.cloneNode(true);

  const originalCanvases = chartRef.current.querySelectorAll("canvas");
  const clonedCanvases = cloned.querySelectorAll("canvas");

  originalCanvases.forEach((canvas, i) => {
    const img = document.createElement("img");
    img.src = canvas.toDataURL("image/png");
    img.style.maxWidth = "100%";
    clonedCanvases[i].replaceWith(img);
  });

  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.top = "-9999px";
  wrapper.style.background = "#fff";
  wrapper.style.padding = "8px";
  wrapper.appendChild(cloned);
  document.body.appendChild(wrapper);

  const canvas = await html2canvas(wrapper, { backgroundColor: "#fff" });
  const base64 = canvas.toDataURL("image/png");

  document.body.removeChild(wrapper);

  return { type, image: base64 };
};

export const SurveyBody = ({
  symptomData,
  diagnosticData,
  answers,
  setAnswers,
  handleSubmit,
  disableResults,
  showChart1,
  showChart2,
  chart1Ref,
  chart2Ref,
  graphData,
  display,
  disableIncludeTest,
  settings,
  unanswered,
  setUnanswered,
  setOpen,
  isIframe,
  userInfo,
  setUserInfo,
  resultId,
  setDisplay,
  setDisableResults,
  setDisableIncludeTest,
  phase,
  setPhase,
  resultClickCountRef,
  setLoading,
  setDiagnosticData,
  setSymptomData,
  isMobileDevice,
  busy = false,
  beginAction,
  endAction,
}) => {
  const questionRefs = useRef({});
  const didLoadQuestions = useRef(false);

  const initialData = useCallback(async () => {
    try {
      const data = await getSurveyQuestions();
      if (data.isSuccess) {
        setDiagnosticData(data.data.DIAGNOSTICS);
        setSymptomData(data.data.SYMPTOMS);
        setAnswers({});
      } else {
        errorToast(data.message || "Failed to load questions");
      }
    } catch (err) {
      errorToast("Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, [setLoading, setDiagnosticData, setSymptomData, setAnswers]);

  useEffect(() => {
    if (didLoadQuestions.current) return;
    didLoadQuestions.current = true;
    initialData();
  }, [initialData]);

  useEffect(() => {
    if (unanswered.length > 0) {
      const firstId = unanswered[0];
      const el = questionRefs.current[firstId];

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [unanswered]);

  const handleAnswer = (questionId, answer) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: answer,
    }));
    setUnanswered?.((prev) => prev.filter((id) => id !== questionId));
  };

  const setPriorAnswer = (value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [PRIOR_DIAGNOSIS_ID]: value,
    }));
    setUnanswered?.((prev) => prev.filter((id) => id !== PRIOR_DIAGNOSIS_ID));
  };

  const handleChartDisplay = () => {
    setDisplay((prev) => !prev);

    setDisableResults(false);
    setDisableIncludeTest(false);

    if (phase === 1) {
      setPhase(2);
      resultClickCountRef.current = 0;
    }
  };

  const printDiv = async () => {
    if (!resultId) {
      errorToast("Survey result ID not found.");
      return;
    }
    if (!beginAction || !beginAction()) return;

    try {
      /** @type {{ type: string, image: string }[]} */
      const chartBase64Array = [];

      if (chart1Ref.current) {
        const chart1 = await captureChartAsBase64(chart1Ref, "symptoms");
        if (chart1) chartBase64Array.push(chart1);
      }
      if (chart2Ref.current && display) {
        const chart2 = await captureChartAsBase64(chart2Ref, "diagnostic");
        if (chart2) chartBase64Array.push(chart2);
      }

      const res = await getSurveyPdf({
        id: resultId,
        chartData: chartBase64Array,
        isMobile: isMobileDevice(),
        logo: await svgToPngBase64(logo),
      });

      if (!res?.isSuccess || !res?.data) {
        errorToast(res?.message || "Failed to generate PDF");
        return;
      }

      const pdfWindow = window.open("", "_blank");
      if (!pdfWindow) {
        errorToast("Allow pop-ups to view the PDF.");
        return;
      }
      pdfWindow.document.write(
        `<iframe width="100%" height="100%" src="data:application/pdf;base64,${res.data}"></iframe>`,
      );
      pdfWindow.document.close();
    } catch (err) {
      console.error("Error generating PDF:", err);
      errorToast("Error generating PDF");
    } finally {
      endAction?.();
    }
  };
  return (
    <>
      <div className="container">
        <SurveyIntro />

        <ul className=" quetions_list flex flex-col items-center">
          {symptomData.map((q) => (
            <li
              key={q.id}
              ref={(el) => (questionRefs.current[q.id] = el)}
              className={`w-100 p-[10px] py-[8px] rounded-[8px] flex justify-between items-center${
                unanswered.includes(q.id) ? " question_missing" : ""
              }`}
              style={{
                maxWidth: "1050px",
                alignItems: "center",
                margin: "8px 0px",
              }}
            >
              <p
                className="text-[16px] font-[600] flex gap-[10px] text-[#3C4049]"
                style={{
                  whiteSpace: "pre-line",
                }}
              >
                <span className="text-[15px] font-[700] !text-[#3C4049]">
                  Q.{q.sort_order}
                </span>{" "}
                <span dangerouslySetInnerHTML={{ __html: q.question }}></span>
              </p>
              <div className="flex gap-[10px]">
                <button
                  onClick={() => handleAnswer(q.id, true)}
                  className={`${
                    answers[q.id] === true ? "active" : ""
                  } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                  style={{
                    maxWidth: "80px",
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleAnswer(q.id, false)}
                  className={`${
                    answers[q.id] === false ? "active" : ""
                  } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                  style={{
                    maxWidth: "80px",
                  }}
                >
                  No
                </button>
              </div>
            </li>
          ))}
          <li
            ref={(el) => (questionRefs.current[PRIOR_DIAGNOSIS_ID] = el)}
            className={`w-100 p-[10px] py-[8px] rounded-[8px] flex justify-between items-center${
              unanswered.includes(PRIOR_DIAGNOSIS_ID) ? " question_missing" : ""
            }`}
            style={{
              maxWidth: "1050px",
              alignItems: "center",
              margin: "8px 0px",
            }}
          >
            <p className="text-[16px] font-[600] flex gap-[10px] text-[#3C4049]">
              <span dangerouslySetInnerHTML={{ __html: PRIOR_DIAGNOSIS_QUESTION }}></span>
            </p>
            <div className="flex flex-col gap-[8px]">
              <div className="flex gap-[10px]">
                {["Yes", "No"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setPriorAnswer(option)}
                    className={`${
                      answers[PRIOR_DIAGNOSIS_ID] === option ? "active" : ""
                    } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                    style={{ maxWidth: "80px" }}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPriorAnswer("Not sure")}
                className={`${
                  answers[PRIOR_DIAGNOSIS_ID] === "Not sure" ? "active" : ""
                } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                style={{ maxWidth: "170px", width: "170px" }}
              >
                Not sure
              </button>
            </div>
          </li>
        </ul>

        {isIframe && (
          <div style={{ margin: "8px 0px", width: "100%" }}>
            <UserInfo userInfo={userInfo} setUserInfo={setUserInfo} />
          </div>
        )}

        {showChart1 && (
          <div
            ref={chart1Ref}
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="flex justify-center items-center w-full h-full">
              <Chart
                value={graphData.resultOf8Questions.percentageOf8Que}
                score={graphData.symptomsSum}
              />
            </div>
          </div>
        )}

        {display && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
            className="my-[10px]"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                gap: "15px",
                top: 0,
              }}
              className="flex-wrap"
            >
              <button
                className="incude_optional_diagnostic_test"
                onClick={handleChartDisplay}
                disabled={disableIncludeTest || busy}
                style={{
                  opacity: disableIncludeTest || busy ? 0.7 : 1,
                  pointerEvents: disableIncludeTest || busy ? "none" : "",
                  width: "fit-content",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                  backgroundColor: disableIncludeTest ? "#d5d5d5" : "",
                }}
              >
                {settings?.survey_optional_test_button_label ||
                  "Include Optional Diagnostic Test?"}
              </button>
            </div>
          </div>
        )}

        <ul className="my-[10px] quetions_list flex flex-col items-center">
          {display && (
            <>
              {diagnosticData.map((q) => (
                <li
                  key={q.id}
                  className="w-100 bg-[#F0FAF9] p-[10px] py-[8px] rounded-[8px] flex justify-between items-center"
                  style={{
                    maxWidth: "1050px",
                    alignItems: "center",
                    margin: "8px 0px",
                  }}
                >
                  <p
                    className="text-[15px] font-[600] flex gap-[10px] text-[#3C4049]"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    <span className="text-[15px] font-[700] text-[#3C4049]">
                      Q.{q.sort_order}
                    </span>
                    <span
                      dangerouslySetInnerHTML={{ __html: q.question }}
                    ></span>
                  </p>
                  <div className="flex gap-[10px]">
                    <button
                      onClick={() => {
                        handleAnswer(q.id, true);
                        setAnswers((prev) => ({
                          ...prev,
                          [q.id + "_unknown"]: false,
                        }));
                      }}
                      className={`${
                        answers[q.id] === true && !answers[q.id + "_unknown"]
                          ? "active"
                          : ""
                      } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                      style={{
                        maxWidth: "80px",
                      }}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => {
                        handleAnswer(q.id, false);
                        setAnswers((prev) => ({
                          ...prev,
                          [q.id + "_unknown"]: false,
                        }));
                      }}
                      className={`${
                        answers[q.id] === false && !answers[q.id + "_unknown"]
                          ? "active"
                          : ""
                      } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                      style={{
                        maxWidth: "80px",
                      }}
                    >
                      No
                    </button>

                    <button
                      onClick={() => {
                        handleAnswer(q.id, false);
                        setAnswers((prev) => ({
                          ...prev,
                          [q.id + "_unknown"]: true,
                        }));
                      }}
                      className={`${
                        answers[q.id + "_unknown"] ? "active" : ""
                      } md:max-h-[30px] max-h-[25px] md:text-[16px] text-[14px]`}
                      style={{
                        maxWidth: "100px",
                      }}
                    >
                      Unknown
                    </button>
                  </div>
                </li>
              ))}
            </>
          )}
        </ul>

        {display && showChart2 && (
          <div
            ref={chart2Ref}
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="flex justify-center items-center w-full h-full">
              <Chart2
                value={graphData.resultOf11Questions.percentageOf11Que}
                score={graphData.diagnosticsSum}
                defaultValue={graphData.resultOf8Questions?.calculate}
              />
            </div>
          </div>
        )}
      </div>

      <div className="survey_actions my-[10px]">
          <button
            className={`${
              disableResults ? "disable_results" : ""
            } show_results`}
            onClick={handleSubmit}
            disabled={disableResults || busy}
            style={{
              width: "fit-content",
              paddingLeft: "20px",
              paddingRight: "20px",
            }}
          >
            {settings?.survey_result_button_label || "Show Results"}
          </button>
          {showChart1 || showChart2 ? (
            <>
              <button
                className="show_results"
                onClick={() => {
                  if (!busy) setOpen(true);
                }}
                disabled={busy}
                style={{
                  width: "fit-content",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                }}
              >
                {settings?.survey_email_button_label || "Send Email"}
              </button>
              <button
                className="show_results"
                onClick={printDiv}
                disabled={busy}
                style={{
                  width: "fit-content",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                }}
              >
                {settings?.survey_print_button_label || "Print"}
              </button>
            </>
          ) : null}
      </div>
      {showChart1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
          className="my-[10px]"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              gap: "15px",
              top: 0,
            }}
            className="flex-wrap"
          >
            {!display && (
              <button
                className="incude_optional_diagnostic_test"
                onClick={handleChartDisplay}
                disabled={disableIncludeTest || busy}
                style={{
                  opacity: disableIncludeTest || busy ? 0.7 : 1,
                  pointerEvents: disableIncludeTest || busy ? "none" : "",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                  backgroundColor: disableIncludeTest ? "#d5d5d5" : "",
                }}
              >
                {settings?.survey_optional_test_button_label ||
                  "Include Optional Diagnostic Test?"}
              </button>
            )}
          </div>
        </div>
      )}
      <div className="container">
        <p className="font-[700] text-[#525f7f] py-[10px] md:text-[17px] text-[16px] md:pl-3 pl-3 md:text-center">
          A-Tool&copy; screening survey is for educational/informational
          purposes only and does not constitute medical advice or a diagnosis.
          Please consult your healthcare provider about your specific health
          issues and to determine the need to see rheumatologist for next steps.
        </p>
        <p className="font-[700] text-[#525f7f] py-[10px] md:text-[17px] text-[16px] md:pl-3 pl-3 md:text-center">
          A-Tool&copy; was developed by{" "}
          <b className="text-[#dc2626] !font-bold ">Abhijeet Danve, MD, MHS</b>,
          a physician-scientist and Associate Professor of Medicine at Yale
          University.
        </p>
      </div>
    </>
  );
};
